import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { getEnv } from '../config/environment';
import {
  APPLICATION_LOCAL_STORAGE,
  REDIRECTION_PAGE,
} from '../common/constants/constants';

const { base } = getEnv();

const rawBaseQuery = fetchBaseQuery({
  baseUrl: base,
  prepareHeaders: (headers) => {
    // TODO(security): Auth tokens should ideally be stored in HttpOnly cookies
    // set by the backend, not in localStorage. This pattern is preserved from
    // the legacy app for backward compatibility with the existing auth backend.
    const authToken = localStorage.getItem(APPLICATION_LOCAL_STORAGE.AUTH_TOKEN);
    const authType = localStorage.getItem(APPLICATION_LOCAL_STORAGE.PING_ACCESS_TOKEN)
      ? 'PING_AUTH'
      : 'PASSCODE_AUTH';

    headers.set('Accept', 'application/json');
    headers.set('Content-Type', 'application/json');
    headers.set('authType', authType);

    if (authToken) {
      headers.set('x-authtoken', authToken);
      headers.set('Authorization', authToken);
    }

    return headers;
  },
});

/**
 * Wrapper around fetchBaseQuery that handles 401 responses
 * by clearing auth state and redirecting to login.
 * Replaces the old `checkStatus()` function from ApiManager.js.
 */
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const hasAccess = localStorage.getItem('access');
    if (hasAccess !== null) {
      localStorage.removeItem('access');
      localStorage.removeItem(APPLICATION_LOCAL_STORAGE.AUTH_TOKEN);
      localStorage.removeItem(APPLICATION_LOCAL_STORAGE.PING_ACCESS_TOKEN);
      // Redirect to login — full page reload to clear all state
      window.location.replace(REDIRECTION_PAGE.LOGIN_PAGE);
    }
  } else if (result.data) {
    localStorage.setItem('access', 'true');
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Auth',
    'Users',
    'Roles',
    'Epms',
    'Dashboard',
    'AutoProvisioning',
    'Falcon',
    'Projects',
    'Jobs',
    'JobLogs',
    'GlobalJobs',
    'Conditions',
  ],
  endpoints: () => ({}),
});
